<template lang="pug">
multiselect.outlet-selector(
    :options="shouldUseAreaFilters ? filteredOutlets : outlets",
    v-model.number="activeOutlet",
    track-by="id",
    label="name",
    :placeholder="outletPlaceholder"
    :loading="loading || isLoadingFiltered"
    :internalSearch="false"
    :showLabels="false"
    @search-change="loadOutlets"
)
    template(slot="option" slot-scope="props")
        span.block {{ props.option.name }}
        small.text-muted
            | {{ describeOutlet(props.option) }}
</template>

<script>
import { mapGetters, mapActions, mapMutations } from 'vuex';
import {
    hasAreaFilters,
    shouldValidateOnLoad,
    shouldValidateOnFilterChange,
    isSelectionValid,
} from './outlet-selector-rules';

export default {
    props: {
        outlet: {
            type: Number | String,
            default: 0,
        },

        allowAll: {
            type: Boolean,
            default: true,
        },

        areaFilters: {
            type: Object,
            default: () => ({ region_ids: [], trading_area_ids: [] }),
        },
    },

    data() {
        let all_outlets = `All ${this.trans('general.outlets')}`;
        let select_outlets = `Select ${this.trans('general.outlets')}`;

        return {
            all_outlets,
            select_outlets,
            filteredOutlets: [],
            isLoadingFiltered: false,
        };
    },

    computed: {
        activeOutlet: {
            get() {
                const outlet_id = Number(this.$store.getters.activeOutlet) | 0;
                return _.find(this.outlets, { id: outlet_id });
            },

            set(value) {
                this.$store.commit('setActiveOutlet', value.id);
            },
        },

        api() {
            return Laroute.route('franchise.vue.outlets');
        },

        ...mapGetters(['outletList']),

        loading() {
            return this.onLoading('fetch-report-outlet');
        },

        outlets: {
            get() {
                var all = [
                    {
                        id: 0,
                        name: this.all_outlets,
                    },
                ];

                if (this.allowAll && this.outletList.length !== 1) {
                    return _.concat(all, this.outletList);
                }

                return this.outletList;
            },

            set(value) {
                return this.outletList;
            },
        },

        shouldUseAreaFilters() {
            return this.hasAreaFilters;
        },

        hasAreaFilters() {
            return hasAreaFilters(this.areaFilters);
        },

        outletPlaceholder() {
            if (
                this.shouldUseAreaFilters &&
                this.filteredOutlets.length === 0 &&
                !this.isLoadingFiltered
            ) {
                return 'No outlets in selected areas';
            }
            return this.select_outlets;
        },
    },

    watch: {
        areaFilters: {
            handler(newVal, oldVal) {
                if (this.hasAreaFilters) {
                    this.fetchFilteredOutlets();
                } else {
                    this.filteredOutlets = [];
                    if (shouldValidateOnFilterChange(oldVal, newVal)) {
                        this.validateCurrentSelection();
                    }
                }
            },
            deep: true,
            immediate: true,
        },
    },

    async created() {
        await this.getOutletList();

        if (shouldValidateOnLoad(this.areaFilters)) {
            this.validateCurrentSelection();
        }

        if (!this.allowAll || this.outletList.length === 1) {
            this.setActiveFirstOutlet();
        }
    },

    mounted() {
        this.loadOutlets();

        if (this.hasAreaFilters) {
            this.fetchFilteredOutlets();
        }
    },

    methods: {
        ...mapActions(['getOutletList']),
        ...mapMutations(['setActiveFirstOutlet']),

        describeOutlet(outlet) {
            let desc = [];

            if (outlet.code) {
                desc.push(outlet.code);
            }

            if (outlet.details && outlet.details.company_name) {
                desc.push(outlet.details.company_name);
            }

            return desc.join(' · ');
        },

        loadOutlets: _.debounce(async function(query) {
            if (this.shouldUseAreaFilters) {
                await this.searchFilteredOutlets(query);
            } else {
                await this.getOutletList({ query });
            }
        }, 500),

        async searchFilteredOutlets(query) {
            this.isLoadingFiltered = true;

            try {
                const params = {
                    limit: 1000,
                    query: query || '',
                };

                if (this.areaFilters.region_ids.length > 0) {
                    params.region_ids = this.areaFilters.region_ids;
                }

                if (this.areaFilters.trading_area_ids.length > 0) {
                    params.trading_area_ids = this.areaFilters.trading_area_ids;
                }

                const response = await axios.get(
                    Laroute.route('franchise.vue.outlets'),
                    {
                        params,
                    },
                );

                let filteredData = response.data.data || [];

                if (this.allowAll && filteredData.length >= 1) {
                    filteredData = [
                        {
                            id: 0,
                            name: this.all_outlets,
                        },
                        ...filteredData,
                    ];
                }

                this.filteredOutlets = filteredData;
            } catch (error) {
                console.error('Error searching filtered outlets:', error);
                this.filteredOutlets = [];
            } finally {
                this.isLoadingFiltered = false;
            }
        },

        async fetchFilteredOutlets() {
            if (!this.hasAreaFilters) {
                this.filteredOutlets = [];
                return;
            }

            await this.searchFilteredOutlets('');
        },

        validateCurrentSelection() {
            const currentOutletId =
                Number(this.$store.getters.activeOutlet) | 0;

            if (currentOutletId === 0) {
                return;
            }

            const currentOutlets = this.shouldUseAreaFilters
                ? this.filteredOutlets
                : this.outlets;
            if (!isSelectionValid(currentOutletId, currentOutlets)) {
                this.$store.commit('setActiveOutlet', 0);
            }
        },
    },
};
</script>

<style lang="scss">
.outlet-selector {
    .multiselect__select {
        height: 36px;
    }

    .multiselect__tags {
        min-height: 38px;
    }
}
</style>



// WEBPACK FOOTER //
// resources/assets/js/components/OutletSelector.vue